/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {DropdownCheckbox} from './DropdownCheckbox';
import IonCheckbox from './IonCheckbox';
import {AgGridType} from './IonGrid';
import {getPageProxy, SelectedRows} from './IonGridUtils';
import useGridEventListener from './useGridEventListener';

export interface GridHeaderCheckboxTypes extends SelectedRows {
  onChange?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  gridApi?: AgGridType;
  enableSelectAll?: boolean;
  pageSize?: number;
  isClientModel?: boolean;
}

export interface CheckboxState {
  checked?: boolean;
  indeterminate?: boolean;
}

/**
 * @GridHeaderCheckbox
 *
 * @param {object} gridApi the gridApi so a listener can be placed on it
 * for checked, unchecked, and indeterminate states in the header checkbox
 *
 * ```typescript
 *  import {GridHeaderCheckbox} from '@ion/dls/src/grid/';
 *
 *  <GridHeaderCheckbox gridApi={gridApi} />
 *
 *  <GridHeaderCheckbox gridApi={gridApi}
 *    selectAll={true}
 *    pageSize={50}
 *    totalCount={count} />
 *
 * ```
 */
export const GridHeaderCheckbox = ({
  gridApi,
  totalRowCount,
  pageSize,
  setAreTotalRowsSelected,
  enableSelectAll = false,
  isClientModel = false
}: GridHeaderCheckboxTypes) => {
  const [checkedState, setCheckedState] = useState<CheckboxState>({
    checked       : false,
    indeterminate : false
  });

  const totalRowCountRef = useRef(totalRowCount);

  useEffect(() => {
    totalRowCountRef.current = totalRowCount;
  }, [totalRowCount]);

  const selectClientRows = useCallback((isSelectionChanged = false) => {
    if (isSelectionChanged) {
      const selected = gridApi.getSelectedNodes()?.length;
      const total    = gridApi.getDisplayedRowCount();
      if (selected === 0) {
        setCheckedState({
          checked       : false,
          indeterminate : false
        });
      } else if (total === selected) {
        setCheckedState({
          checked       : true,
          indeterminate : false
        });
      } else {
        setCheckedState({
          checked       : true,
          indeterminate : true
        });
      }
    } else {
      const disabledRowIds = [];
      const gridRootElement = document.querySelector(`[grid-id="${gridApi.getGridId()}"]`);
      gridRootElement.querySelectorAll('.ag-row.disabled-row').forEach(rowElement => {
        const rowIndex = rowElement.getAttribute('row-index');
        if (rowIndex !== null) {
          const {id} = gridApi.getDisplayedRowAtIndex(Number(rowIndex)) || {};
          if (id && !disabledRowIds.includes(id)) {
            disabledRowIds.push(id);
          }
        }
      });
      gridApi.forEachNodeAfterFilterAndSort((row: {setSelected: Function; data: any, id: string}) => {
        if (!disabledRowIds.includes(row.id)) {
          row.setSelected(!checkedState.checked || checkedState.indeterminate);
        }
      });
      selectClientRows(true);
    }
  }, [gridApi, checkedState]);

  useGridEventListener(gridApi, 'paginationChanged', onPaginationChanged);
  useGridEventListener(gridApi, 'selectionChanged', onPaginationChanged);

  // calculate if the header checkbox should be checked when user pages
  function onPaginationChanged() {
    isClientModel ? selectClientRows(true) : setHeaderCheckboxState();
  };

  /**
   * @setHeaderCheckboxState
   *
   * Function that calculates if the header checkbox should
   * be checked or not based on the current page.
   */
  function setHeaderCheckboxState() {
    if (gridApi) {
      const {
        bottomDisplayedRowIndex: lastRowIndex,
        topDisplayedRowIndex: firstRowIndex
      } = getPageProxy(gridApi, totalRowCountRef.current);
      const selected = gridApi.getSelectedNodes();
      const selectedOnPage = selected.filter((item: { [key: string]: any }): boolean =>
        item.rowIndex >= firstRowIndex && item.rowIndex <= lastRowIndex);
      const pageRows = (lastRowIndex - firstRowIndex) + 1;
      if (!selectedOnPage.length) {
        setCheckedState({
          checked       : false,
          indeterminate : false
        });
      } else if (selectedOnPage.length < pageRows) {
        setCheckedState({
          checked       : false,
          indeterminate : true
        });
      } else {
        setCheckedState({
          checked       : true,
          indeterminate : false
        });
      }
    }
  }

  function selectRows(select: boolean = true, applyToAllRows: boolean = false) {
    setCheckedState({
      checked       : select,
      indeterminate : false
    });
    if (!applyToAllRows) {
      const {
        bottomDisplayedRowIndex: lastRowIndex = 19,
        topDisplayedRowIndex: firstRowIndex = 0
      } = getPageProxy(gridApi, totalRowCountRef.current);
      gridApi.forEachNode((row: { setSelected: Function, rowIndex: number }) => {
        const {rowIndex: index} = row;
        if (index <= lastRowIndex && index >= firstRowIndex) {
          row.setSelected(select);
        };
      });
    } else {
      gridApi.forEachNode((row: { setSelected: Function }) => row.setSelected(select));
    }
    setHeaderCheckboxState();
  }

  const toggleSelectPageRows = useCallback(function(): void {
    if (isClientModel) {
      selectClientRows();
    } else {
      selectRows(!checkedState.checked);
    }
  }, [checkedState.checked, isClientModel, selectClientRows]);

  if (enableSelectAll) {
    return (
      <DropdownCheckbox checkboxState={checkedState}
        totalRowCount={totalRowCount}
        pageSize={pageSize}
        toggleSelectingRows={selectRows}
        setAreTotalRowsSelected={setAreTotalRowsSelected} />
    );
  };

  return (
    <IonCheckbox onClick={toggleSelectPageRows}
      checked={checkedState.checked}
      indeterminate={checkedState.indeterminate} />
  );
};

export default memo(GridHeaderCheckbox);
